import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Modal,
    SxProps,
    Typography,
    useTheme,
} from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';

export type ConfirmWarningModalProps = {
    title: string;
    message: string;
    validationButtonText: string;
    submitingProcess: boolean;
    actionOnValidation: () => void;
    handleCloseModal: () => void;
    modalOpen: boolean;
    warningLevel?: 'primary' | 'info' | 'error';
    sx?: SxProps;
};

export const ConfirmWarningModal: React.FunctionComponent<ConfirmWarningModalProps> = function ({
    title,
    message,
    validationButtonText,
    submitingProcess,
    actionOnValidation,
    handleCloseModal,
    modalOpen,
    warningLevel = 'error',
    sx,
}) {
    const { palette } = useTheme();

    return (
        <Modal
            open={modalOpen}
            onClose={handleCloseModal}
            aria-labelledby="create-modal-title"
            aria-describedby="create-modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 500,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '12px',
                    ...sx,
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={handleCloseModal}
                    sx={{
                        fontSize: '16px',
                        position: 'absolute',
                        right: '20px',
                        top: '35px',
                        color: palette.grey[500],
                    }}
                >
                    <FaOptionIcon icon={['fas', 'close']} />
                </IconButton>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingTop: '24px',
                        gap: '8px',
                    }}
                >
                    {warningLevel === 'error' && (
                        <Box
                            sx={{
                                padding: '10px',
                                gap: '2px',
                                background: palette[warningLevel].light,
                                borderRadius: '12px',
                            }}
                        >
                            <FaOptionIcon
                                icon={['fasl', 'triangle-exclamation']}
                                color={palette[warningLevel].main}
                                sx={{ fontSize: '21px' }}
                            />
                        </Box>
                    )}

                    <Typography
                        variant="l2dh6"
                        sx={{ textAlign: 'center' }}
                    >
                        {title}
                    </Typography>

                    {message && (
                        <Typography
                            variant="l2dbodym"
                            sx={{ textAlign: 'center' }}
                        >
                            {message}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            padding: '26px 30px',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: '16px' }}>
                            {submitingProcess ? (
                                <Button
                                    variant="contained"
                                    sx={{
                                        padding: '16px 24px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minWidth: '6rem',
                                        fontSize: '16px',
                                        background: palette[warningLevel].dark,
                                        ':hover': {
                                            background: palette[warningLevel].main,
                                        },
                                    }}
                                >
                                    <CircularProgress
                                        color="info"
                                        sx={{
                                            maxHeight: '15px',
                                            maxWidth: '15px',
                                            color: 'common.white',
                                        }}
                                    />
                                </Button>
                            ) : (
                                <Button
                                    onClick={actionOnValidation}
                                    sx={{
                                        padding: '16px 24px',
                                        fontSize: '16px',
                                        background: palette[warningLevel].dark,
                                        ':hover': {
                                            background: palette[warningLevel].main,
                                        },
                                        transition: 'all ease-in-out 300ms',
                                    }}
                                    variant="contained"
                                >
                                    {validationButtonText}
                                </Button>
                            )}
                            <Button
                                onClick={handleCloseModal}
                                sx={{
                                    padding: '16px 24px',
                                    fontSize: '16px',
                                    border: `1px solid ${palette.grey[400]}`,
                                    color: palette.grey[700],
                                    transition: 'all ease-in-out 300ms',
                                }}
                                variant="outlined"
                            >
                                Annuler
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};
